import { useEffect, useRef, useState, type ReactNode } from "react";
import { docT } from "./doc-locale";
import "./doc-evening.css";

// The evening of the webinar, rebuilt for F93 (Pavel 2026-08-07).
//
// F91 scaled each axis segment's HEIGHT by its minutes. On a real evening that
// draws a 35-minute phase as a 245px column of nothing next to one line of
// text: "strasne velky mezery, nevypada to vizualne vubec dobre". Proportion
// is now carried HORIZONTALLY - a weight bar whose width is the phase's share
// of the longest phase - so the eye still reads "odsud potud tohle" and no row
// is taller than its own content.
//
// The second half of the same feedback: "musi to byt klikatelny. Otvirat mi to
// nejakym zpusobem ty odrazky." So the phase is not a section somewhere else
// on the page any more: it is a <details> panel that opens IN the evening,
// with its minute axis inside. The compact map on top is the navigator; a map
// row opens its panel and scrolls to it. Native <details> is deliberate: it
// gives keyboard and screen-reader behaviour for free AND makes an external
// jump work, because `jumpToDocId` already opens a details it lands on.
//
// Three laws carried over from F91 and kept: the map and the panels render
// from the SAME `<ph>` nodes (a dead map row cannot be authored), every
// affordance answers hover, keyboard and tap, and nothing is injected.

function reduceMotion(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
    : false;
}

export interface DocEveningPhase {
  id: string | undefined;
  n: string;
  name: string;
  /** Clock start, e.g. "0:35". */
  start: string;
  /** Duration in minutes (parsed), or null when the attr is not a number. */
  minutes: number | null;
  /** Raw min attr for display when it is not parseable. */
  minLabel: string;
  /** How many `<mi>` steps the phase carries: the map's depth signal. */
  steps: number;
  body: ReactNode;
}

/** "12 kroku" / "2 kroky" / "1 krok" - Czech counts three ways, so does this. */
function stepsLabel(count: number): string {
  const key = count === 1 ? "doc.evening.step1" : count < 5 ? "doc.evening.step2" : "doc.evening.step5";
  return `${count} ${docT(key)}`;
}

/** The phase's share of the longest phase, floored so a 2-minute bar exists. */
function share(minutes: number | null, longest: number): number {
  return longest > 0 && minutes ? Math.max(6, Math.round((minutes / longest) * 100)) : 0;
}

/**
 * The whole evening: the compact map, then one open-able panel per phase.
 *
 * 🔴 The open state lives in the DOM, not in React. `<details open>` is
 * rendered once and never touched by a re-render, so opening and closing works
 * in a static render of this document too (the visual fixture ships as static
 * markup with no client JS, and a state-driven accordion silently does nothing
 * there). It also means `jumpToDocId` from a `<fstage to="ph-6">` opens the
 * panel it lands on for free, with no coordination between two components.
 * This component therefore holds NO state; only the map does.
 */
export function DocEveningBlock(props: { title?: string; total?: string; phases: DocEveningPhase[] }) {
  const { phases } = props;
  const rootRef = useRef<HTMLDivElement | null>(null);
  const longest = phases.reduce((top, phase) => Math.max(top, phase.minutes ?? 0), 0);

  if (phases.length === 0) return null;
  return (
    <div className="doc-evening" ref={rootRef}>
      <DocEveningMap title={props.title} total={props.total} phases={phases} longest={longest} rootRef={rootRef} />
      <div className="doc-ph-stack">
        {phases.map((phase, index) => (
          <DocPhasePanel key={`${phase.n}-${index}`} phase={phase} share={share(phase.minutes, longest)} />
        ))}
      </div>
    </div>
  );
}

/**
 * Level one: the whole evening in twelve one-line rows. Clicking a row opens
 * its panel and scrolls to it; the row of the panel being read lights up.
 */
function DocEveningMap(props: {
  title?: string;
  total?: string;
  phases: DocEveningPhase[];
  longest: number;
  rootRef: { current: HTMLDivElement | null };
}) {
  const { phases } = props;
  const [active, setActive] = useState<number | null>(null);
  const [allOpen, setAllOpen] = useState(true);

  const panels = (): HTMLDetailsElement[] => [
    ...(props.rootRef.current?.querySelectorAll<HTMLDetailsElement>("details.doc-ph") ?? [])
  ];

  // Follow the reading position: whichever panel is nearest the top of the
  // viewport owns its map row (the DocTimeline law).
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return undefined;
    const targets = phases
      .map((phase, index) => ({ index, el: phase.id ? document.getElementById(phase.id) : null }))
      .filter((entry): entry is { index: number; el: HTMLElement } => entry.el !== null);
    if (targets.length === 0) return undefined;
    const visible = new Map<Element, number>();
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) visible.set(entry.target, entry.boundingClientRect.top);
        else visible.delete(entry.target);
      }
      if (visible.size === 0) return;
      const top = [...visible.entries()].sort((a, b) => a[1] - b[1])[0][0];
      const match = targets.find((entry) => entry.el === top);
      if (match) setActive(match.index);
    }, { rootMargin: "-8% 0px -70% 0px", threshold: 0 });
    targets.forEach((entry) => observer.observe(entry.el));
    return () => observer.disconnect();
  }, [phases]);

  // The button must never lie about what it will do. `toggle` does not bubble,
  // but it does have a capture phase, so one listener on the container sees
  // every panel the reader opens or closes by hand.
  useEffect(() => {
    const root = props.rootRef.current;
    if (!root) return undefined;
    const sync = () => setAllOpen(panels().some((panel) => panel.open));
    root.addEventListener("toggle", sync, true);
    sync();
    return () => root.removeEventListener("toggle", sync, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phases]);

  const jump = (phase: DocEveningPhase, index: number) => {
    setActive(index);
    if (!phase.id) return;
    const el = document.getElementById(phase.id);
    if (!el) return;
    if (el instanceof HTMLDetailsElement) el.open = true;
    el.scrollIntoView({ behavior: reduceMotion() ? "auto" : "smooth", block: "start" });
  };

  return (
    <nav className="doc-emap" aria-label={props.title || docT("doc.evening.aria")}>
      <p className="doc-emap-head">
        <span className="doc-emap-title">{props.title || docT("doc.evening.aria")}</span>
        {props.total && <span className="doc-emap-total">{props.total}</span>}
        <button
          type="button"
          className="doc-emap-all"
          onClick={() => {
            const next = !allOpen;
            panels().forEach((panel) => { panel.open = next; });
            setAllOpen(next);
          }}
        >
          {allOpen ? docT("doc.evening.collapseAll") : docT("doc.evening.expandAll")}
        </button>
      </p>
      <ol className="doc-emap-track">
        {phases.map((phase, index) => {
          const width = share(phase.minutes, props.longest);
          const classes = ["doc-emap-row", active === index ? "is-active" : ""].filter(Boolean).join(" ");
          return (
            <li className={classes} key={`${phase.n}-${index}`}>
              <button
                type="button"
                className="doc-emap-jump"
                aria-current={active === index ? "step" : undefined}
                aria-label={`${docT("doc.evening.jump")}: ${[phase.n, phase.name, phase.start, phase.minLabel].filter(Boolean).join(", ")}`}
                onClick={() => jump(phase, index)}
              >
                <span className="doc-emap-time">{phase.start}</span>
                <span className="doc-emap-n">{phase.n}</span>
                <span className="doc-emap-name">{phase.name}</span>
                <span className="doc-emap-bar" aria-hidden="true">
                  {width > 0 && <i style={{ width: `${width}%` }} />}
                </span>
                <span className="doc-emap-min">{phase.minLabel}</span>
                <span className="doc-emap-steps">{phase.steps > 0 ? stepsLabel(phase.steps) : ""}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/**
 * One phase, opening in place. The header IS the toggle (the e-mail card
 * idiom), and it carries the same facts as its map row plus the weight bar, so
 * a reader who scrolled past the map still knows how long this part runs.
 * Open by default: the reader came for the content, and a document that greets
 * them with twelve closed rows is the "extremne strucny" complaint in a new
 * costume. Collapsing is the affordance, not the default.
 */
function DocPhasePanel(props: { phase: DocEveningPhase; share: number }) {
  const { phase } = props;
  return (
    <details id={phase.id} className="doc-ph" open>
      <summary className="doc-ph-head">
        <span className="doc-ph-n" aria-hidden="true">{phase.n}</span>
        <span className="doc-ph-title">
          <span className="doc-ph-name">{phase.name}</span>
          <span className="doc-ph-chips">
            {phase.start && <span className="doc-ph-start">{phase.start}</span>}
            {phase.minLabel && <span className="doc-ph-min">{phase.minLabel}</span>}
            {phase.steps > 0 && <span className="doc-ph-steps">{stepsLabel(phase.steps)}</span>}
            {props.share > 0 && (
              <span className="doc-ph-weight" aria-hidden="true"><i style={{ width: `${props.share}%` }} /></span>
            )}
          </span>
        </span>
        <span className="doc-ph-caret" aria-hidden="true" />
      </summary>
      <div className="doc-ph-body">{phase.body}</div>
    </details>
  );
}

export interface DocMinuteRow {
  /** Minute range chip, e.g. "0-2" or "min 12-15". */
  at: string;
  body: ReactNode;
  /** Swappable concrete example of how the step sounds for THIS client. */
  example?: string;
}

/**
 * The second level: what to say, minute by minute. Pavel 2026-08-07: "pro
 * kazdych 5 minut mi davas 3 slova... potrebuju vedet, co mam dat do kazde
 * minuty, jak to skladat za sebou." A spined list of steps; each step may
 * carry ONE example line, marked as an example so the owner knows the wording
 * is theirs to replace while the structure is not.
 */
export function DocMinutes(props: { title?: string; rows: DocMinuteRow[] }) {
  if (props.rows.length === 0) return null;
  return (
    <div className="doc-minutes">
      {props.title && <p className="doc-minutes-title">{props.title}</p>}
      <ol className="doc-minutes-track">
        {props.rows.map((row, index) => (
          <li className="doc-mi" key={`${row.at}-${index}`}>
            <span className="doc-mi-at">{row.at}</span>
            <span className="doc-mi-dot" aria-hidden="true" />
            <span className="doc-mi-main">
              <span className="doc-mi-body">{row.body}</span>
              {row.example && (
                <span className="doc-mi-eg">
                  <span className="doc-mi-eg-label">{docT("doc.mi.example")}</span>
                  {row.example}
                </span>
              )}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

/**
 * The seam: what must have been said BEFORE this phase and what it prepares
 * AFTER it. Pavel: "vim, jak to na sebe navazuje, co je dusledek." Two cells,
 * one arrow each way; a phase with only one side still renders the side it has.
 */
export function DocSeam(props: { before?: string; after?: string }) {
  if (!props.before && !props.after) return null;
  return (
    <div className="doc-seam">
      {props.before && (
        <p className="doc-seam-cell doc-seam-before">
          <span className="doc-seam-label">{docT("doc.seam.before")}</span>
          {props.before}
        </p>
      )}
      {props.after && (
        <p className="doc-seam-cell doc-seam-after">
          <span className="doc-seam-label">{docT("doc.seam.after")}</span>
          {props.after}
        </p>
      )}
    </div>
  );
}

export interface DocArcMove {
  n: string;
  label: string;
  body: ReactNode;
}

/**
 * The story arc of the whole evening in one band: content that earns the
 * right to sell, the offer introduced without losing the room, the reason to
 * buy DURING the broadcast. Pavel asked for this as its own short element,
 * because it is the thing the twelve phases add up to.
 */
export function DocArc(props: { title?: string; moves: DocArcMove[] }) {
  if (props.moves.length === 0) return null;
  return (
    <div className="doc-arc">
      {props.title && <p className="doc-arc-title">{props.title}</p>}
      <ol className="doc-arc-track" aria-label={props.title || docT("doc.arc.aria")}>
        {props.moves.map((move, index) => (
          <li className="doc-arc-move" key={`${move.n}-${index}`}>
            <span className="doc-arc-n" aria-hidden="true">{move.n}</span>
            <span className="doc-arc-label">{move.label}</span>
            <span className="doc-arc-body">{move.body}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

/**
 * The scope block: what the product actually covers, what the OFFER promises,
 * and therefore how wide the evening's promise has to be. Pavel 2026-08-07:
 * "ty nazvy a ty sliby musej byt odvozeny na zaklade toho, o cem je ten
 * [produkt]... jinak to pritahne lidi, ktery chteji resit jenom ten jeden
 * konkretni problem." The covered areas render as chips so the narrowing is
 * visible on the page, not only in the gate.
 */
export function DocScope(props: {
  title?: string;
  offer?: string;
  covers: string[];
  children: ReactNode;
}) {
  return (
    <div className="doc-scope">
      {props.title && <p className="doc-scope-title">{props.title}</p>}
      {props.covers.length > 0 && (
        <ul className="doc-scope-chips">
          <li className="doc-scope-chip-label">{docT("doc.scope.covers")}</li>
          {props.covers.map((area, index) => (
            <li className="doc-scope-chip" key={`${area}-${index}`}>{area}</li>
          ))}
        </ul>
      )}
      {props.offer && (
        <p className="doc-scope-offer">
          <span className="doc-scope-label">{docT("doc.scope.offer")}</span>
          {props.offer}
        </p>
      )}
      <div className="doc-scope-body">{props.children}</div>
    </div>
  );
}

/** Shared with DocHtml.tsx: `min="9"` -> 9, `min="9 min"` -> 9, junk -> null. */
export function parseMinutes(raw: string | undefined): number | null {
  const match = /\d+(?:[.,]\d+)?/.exec(raw ?? "");
  if (!match) return null;
  const value = Number(match[0].replace(",", "."));
  return Number.isFinite(value) && value > 0 ? value : null;
}

export function minuteLabel(raw: string | undefined): string {
  const value = (raw ?? "").trim();
  if (!value) return "";
  return /^\d+(?:[.,]\d+)?$/.test(value) ? `${value} min` : value;
}
